import { apiUtils, authFetch } from '@src/utils';

export type AccessScope = {
  type: 'site' | 'merchant';
  id: string;
  name: string;
};

export type PermissionSource = {
  groupId: string;
  groupName: string;
  scope: AccessScope;
};

type PermissionStatus = 'granted' | 'not_granted' | 'unknown';

export type PermissionEvidence = {
  name: string;
  status: PermissionStatus;
  sources: PermissionSource[];
};

export type AccessCapability = {
  id: string;
  area: string;
  name: string;
  status: 'allowed' | 'missing' | 'unavailable' | 'unknown';
  reason: string;
  explanation: string;
  permissions: PermissionEvidence[];
};

export type AccessGroup = {
  id: string;
  name: string;
  scope: AccessScope;
  member: boolean;
  permissions: string[];
};

export type PermissionCheck = {
  name: string;
  status: PermissionStatus;
  reason: string;
};

export type CheckUserAccessRequest = {
  email: string;
  siteId: string;
  permission?: string;
};

export type CheckUserAccessResponse = {
  permissionNames: string[];
  capabilities: AccessCapability[];
  groups: AccessGroup[];
  permissionCheck?: PermissionCheck;
  error?: { code: string; message: string };
};

type AccessUser = { id: string; email: string };
type UserLookupResponse = { user: { id: string; email?: string } };

const accessUser = ({ user }: UserLookupResponse): AccessUser => {
  if (!user.email) throw new Error('This user has no email address.');
  return { id: user.id, email: user.email };
};

export const getAccessUserById = (id: string) =>
  authFetch<UserLookupResponse>(
    `${apiUtils.getApiUrl()}/frontend/auth/admin/users.get?id=${encodeURIComponent(id)}`
  ).then(accessUser);

export const getAccessUserByEmail = (email: string) =>
  authFetch<UserLookupResponse>(
    `${apiUtils.getApiUrl()}/frontend/auth/ingrid.service.auth.Authorization/GetUserByEmail`,
    {
      method: 'POST',
      body: JSON.stringify({ email }),
    }
  ).then(accessUser);

export const checkUserAccess = (request: CheckUserAccessRequest) =>
  authFetch<CheckUserAccessResponse>(`${apiUtils.getApiUrl()}/frontend/debug/user-access.check`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
